import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Clock, Zap, ThumbsUp, ThumbsDown, Minus as MinusIcon, MessageSquare, ChevronDown, ChevronUp, ShieldCheck, Loader2, Camera, ArrowLeftRight, Brain } from 'lucide-react';
import type { TradingAnalysis } from '../../types/trading';
import ConfidenceGauge from './ConfidenceGauge';
import ConfluenceDetail from './ConfluenceDetail';
import { useSubmitFeedback, useValidateSignal } from '../../hooks/useTradingAnalysis';
import { tradingApi } from '../../services/tradingApi';

interface SignalCardProps {
  analysis: TradingAnalysis;
  showFeedback?: boolean;
}

const signalConfig = {
  BUY: { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30', icon: TrendingUp },
  SELL: { color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', icon: TrendingDown },
  HOLD: { color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30', icon: Minus },
};

const ratingBadge: Record<string, { label: string; color: string }> = {
  correct: { label: 'Correct', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  incorrect: { label: 'Incorrect', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  partial: { label: 'Partial', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

const validationBadge: Record<string, { label: string; color: string }> = {
  agreed: { label: 'Agreed', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  disagreed: { label: 'Disagreed', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  partial: { label: 'Partial', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

const SignalCard: React.FC<SignalCardProps> = ({ analysis, showFeedback = true }) => {
  const config = signalConfig[analysis.signal] || signalConfig.HOLD;
  const Icon = config.icon;
  const feedbackMutation = useSubmitFeedback();
  const validateMutation = useValidateSignal();
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [showConfluences, setShowConfluences] = useState(false);
  const [showValidationReasoning, setShowValidationReasoning] = useState(false);
  const [screenshotTFs, setScreenshotTFs] = useState<string[]>([]);
  const [showScreenshots, setShowScreenshots] = useState(false);

  const isConfirmation = (analysis as any)._source === 'confirmation';

  // Fetch available screenshots for confirmations
  useEffect(() => {
    if (isConfirmation) {
      tradingApi.getConfirmationScreenshots(analysis.id).then((res) => {
        setScreenshotTFs(res.timeframes);
      }).catch(() => {});
    }
  }, [analysis.id, isConfirmation]);

  const hasConfluenceData = !!(analysis.key_levels || analysis.fair_value_gaps || analysis.indicators);

  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '—';
    return price.toFixed(2);
  };

  const timeAgo = (ts: string) => {
    if (!ts) return '—';
    // Handle various timestamp formats (ISO, Python datetime, MT5-style dots)
    let normalized = ts.replace(/\.\d+$/, '').replace(/^(\d{4})\.(\d{2})\.(\d{2})/, '$1-$2-$3');
    // Ensure UTC interpretation — backend sends UTC timestamps without Z
    if (!/[Z+-]\d{0,4}$/.test(normalized)) normalized += 'Z';
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return '—';
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  };

  return (
    <div className={`rounded-xl border p-5 ${config.bg}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${config.bg}`}>
            <Icon size={24} className={config.color} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-white">{analysis.symbol}</h3>
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border bg-purple-500/20 text-purple-400 border-purple-500/30 font-medium">
                <Brain size={9} />
                AI
              </span>
            </div>
            <p className="text-xs text-slate-400">{analysis.timeframe}</p>
          </div>
        </div>
        <div className="text-right">
          <span className={`text-2xl font-bold ${config.color}`}>{analysis.signal}</span>
          {analysis.cached && (
            <span className="ml-2 text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded">
              cached
            </span>
          )}
        </div>
      </div>

      {/* EA Confirmation Banner */}
      {(analysis as any)._source === 'confirmation' && (
        <div className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg text-xs ${
          (analysis as any)._confirmed
            ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300'
            : 'bg-red-500/10 border border-red-500/20 text-red-300'
        }`}>
          <span className="font-semibold">
            EA {(analysis as any)._confirmed ? 'Confirmed' : 'Rejected'}
          </span>
          <span className="text-slate-400">|</span>
          <span>{(analysis as any)._ea_name}</span>
          {(analysis as any)._signal_source && (
            <>
              <span className="text-slate-400">|</span>
              <span>{(analysis as any)._signal_source}</span>
            </>
          )}
          <span className="text-slate-400">|</span>
          <span>{(analysis as any)._analysis_mode} mode</span>
        </div>
      )}

      {/* Multi-confluence agreement badge */}
      {isConfirmation && (analysis as any)._confluence_sources?.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-amber-400 font-medium">Agreed:</span>
          {((analysis as any)._confluence_sources as string[]).map((src, i, arr) => (
            <span key={src} className="flex items-center gap-1">
              <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-300 ring-1 ring-amber-500/30">
                {src}
              </span>
              {i < arr.length - 1 && <span className="text-slate-500 text-xs">+</span>}
            </span>
          ))}
        </div>
      )}

      {/* Confirmation Screenshots */}
      {isConfirmation && screenshotTFs.length > 0 && (
        <div className="mb-4">
          <button
            onClick={(e) => { e.stopPropagation(); setShowScreenshots(!showScreenshots); }}
            className="flex items-center gap-1.5 text-xs text-teal-400 hover:text-teal-300 transition-colors"
          >
            <Camera size={12} />
            {showScreenshots ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            Charts ({screenshotTFs.join(', ')})
          </button>
          {showScreenshots && (
            <div className="mt-2 space-y-2">
              {screenshotTFs.map((tf) => (
                <div key={tf}>
                  <p className="text-[10px] text-slate-400 mb-1">{tf}</p>
                  <img
                    src={tradingApi.getConfirmationScreenshotUrl(analysis.id, tf)}
                    alt={`${tf} chart`}
                    className="w-full rounded-lg border border-slate-700/50"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confidence */}
      <div className="mb-4">
        <ConfidenceGauge value={analysis.confidence} />
      </div>

      {/* Entry / SL / TP */}
      {analysis.signal !== 'HOLD' && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-400 mb-1">Entry</p>
            <p className="text-sm font-semibold text-white">{formatPrice(analysis.entry)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-red-400 mb-1">Stop Loss</p>
            <p className="text-sm font-semibold text-red-300">{formatPrice(analysis.stop_loss)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-emerald-400 mb-1">Take Profit</p>
            <p className="text-sm font-semibold text-emerald-300">{formatPrice(analysis.take_profit)}</p>
          </div>
        </div>
      )}

      {/* Timeframe Alignment */}
      {analysis.timeframe_alignment && (
        <div className="flex gap-1 mb-3 flex-wrap">
          {(['h4', 'h1', 'm30', 'm15', 'm5'] as const).map((tf) => {
            const val = analysis.timeframe_alignment?.[tf];
            const tfColor =
              val === 'bullish'
                ? 'bg-emerald-500/20 text-emerald-400'
                : val === 'bearish'
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-slate-700 text-slate-400';
            return (
              <span key={tf} className={`text-xs px-2 py-0.5 rounded ${tfColor}`}>
                {tf.toUpperCase()}
              </span>
            );
          })}
          {analysis.timeframe_alignment?.aligned && (
            <span className="text-xs px-2 py-0.5 rounded bg-teal-500/20 text-teal-400">
              <Zap size={10} className="inline mr-1" />
              Aligned
            </span>
          )}
        </div>
      )}

      {/* Confluences Toggle */}
      {hasConfluenceData && (
        <div className="mb-3">
          <button
            onClick={(e) => { e.stopPropagation(); setShowConfluences(!showConfluences); }}
            className="flex items-center gap-1.5 text-xs text-teal-400 hover:text-teal-300 transition-colors"
          >
            {showConfluences ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            Confluences
          </button>
          {showConfluences && (
            <div className="mt-2">
              <ConfluenceDetail analysis={analysis} compact />
            </div>
          )}
        </div>
      )}

      {/* Reasoning */}
      {analysis.reasoning && (
        <p className="text-sm text-slate-300 mb-3 leading-relaxed">{analysis.reasoning}</p>
      )}

      {/* Warnings */}
      {analysis.warnings && analysis.warnings.length > 0 && (
        <div className="mb-3">
          {analysis.warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-400/80">
              {w}
            </p>
          ))}
        </div>
      )}

      {/* Contra Assessment (confirmations only) */}
      {isConfirmation && (analysis as any)._contra_signal && (
        <div className={`mb-3 rounded-lg border p-3 ${
          (analysis as any)._contra_confidence > analysis.confidence
            ? 'bg-amber-500/5 border-amber-500/30'
            : 'bg-slate-800/30 border-slate-700/30'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <ArrowLeftRight size={14} className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-300">Contra Assessment</span>
            {(analysis as any)._contra_confidence > analysis.confidence && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                Stronger contra
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mb-2">
            <span className={`text-sm font-bold ${
              (analysis as any)._contra_signal === 'BUY' ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {(analysis as any)._contra_signal}
            </span>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      (analysis as any)._contra_confidence >= 70 ? 'bg-emerald-500' :
                      (analysis as any)._contra_confidence >= 40 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${(analysis as any)._contra_confidence}%` }}
                  />
                </div>
                <span className="text-xs text-slate-400 w-8 text-right">
                  {(analysis as any)._contra_confidence}%
                </span>
              </div>
            </div>
          </div>
          {((analysis as any)._contra_entry || (analysis as any)._contra_stop_loss || (analysis as any)._contra_take_profit) && (
            <div className="flex gap-3 mb-2 text-[11px]">
              {(analysis as any)._contra_entry && (
                <span className="text-slate-400">Entry: <span className="text-white">{formatPrice((analysis as any)._contra_entry)}</span></span>
              )}
              {(analysis as any)._contra_stop_loss && (
                <span className="text-slate-400">SL: <span className="text-red-300">{formatPrice((analysis as any)._contra_stop_loss)}</span></span>
              )}
              {(analysis as any)._contra_take_profit && (
                <span className="text-slate-400">TP: <span className="text-emerald-300">{formatPrice((analysis as any)._contra_take_profit)}</span></span>
              )}
            </div>
          )}
          {(analysis as any)._contra_reasoning && (
            <p className="text-xs text-slate-400 leading-relaxed">{(analysis as any)._contra_reasoning}</p>
          )}
        </div>
      )}

      {/* Signal Feedback */}
      {showFeedback && (
        <div className="mb-3">
          {analysis.user_rating ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2.5 py-1 rounded-full border ${ratingBadge[analysis.user_rating]?.color || 'bg-slate-700 text-slate-400'}`}>
                {ratingBadge[analysis.user_rating]?.label || analysis.user_rating}
              </span>
              {analysis.user_notes && (
                <span className="text-xs text-slate-400 italic truncate max-w-[200px]">
                  {analysis.user_notes}
                </span>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {feedbackMutation.isError && (
                <p className="text-xs text-red-400 mb-1">Failed to save: {(feedbackMutation.error as Error)?.message || 'Unknown error'}</p>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Rate signal:</span>
                {([
                  { rating: 'correct', icon: ThumbsUp, color: 'hover:bg-emerald-500/20 hover:text-emerald-400' },
                  { rating: 'incorrect', icon: ThumbsDown, color: 'hover:bg-red-500/20 hover:text-red-400' },
                  { rating: 'partial', icon: MinusIcon, color: 'hover:bg-amber-500/20 hover:text-amber-400' },
                ] as const).map(({ rating, icon: RIcon, color }) => (
                  <button
                    key={rating}
                    onClick={(e) => {
                      e.stopPropagation();
                      feedbackMutation.mutate({ analysisId: analysis.id, rating, notes: notes || undefined, isConfirmation });
                    }}
                    disabled={feedbackMutation.isPending}
                    className={`p-1.5 rounded-lg text-slate-500 transition-colors ${color} disabled:opacity-50`}
                    title={rating}
                  >
                    <RIcon size={14} />
                  </button>
                ))}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowNotes(!showNotes); }}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-700 hover:text-slate-300 transition-colors"
                  title="Add notes"
                >
                  <MessageSquare size={14} />
                </button>
              </div>
              {showNotes && (
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Add notes about this signal..."
                  className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 text-xs text-slate-300 placeholder:text-slate-600 resize-none"
                  rows={2}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Signal Validation (analyses only, not confirmations) */}
      {!isConfirmation && showFeedback && <div className="mb-3">
        {analysis.validation_status ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <ShieldCheck size={14} className="text-slate-400" />
              <span className={`text-xs px-2.5 py-1 rounded-full border ${validationBadge[analysis.validation_status]?.color || 'bg-slate-700 text-slate-400'}`}>
                {validationBadge[analysis.validation_status]?.label || analysis.validation_status}
                {analysis.validation_confidence !== null && (
                  <span className="ml-1 opacity-75">{analysis.validation_confidence}%</span>
                )}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border bg-purple-500/20 text-purple-400 border-purple-500/30 font-medium">
                <Brain size={9} />
                AI
              </span>
            </div>
            {analysis.validation_reasoning && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowValidationReasoning(!showValidationReasoning); }}
                  className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-400 transition-colors"
                >
                  {showValidationReasoning ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                  Reviewer reasoning
                </button>
                {showValidationReasoning && (
                  <p className="text-xs text-slate-400 leading-relaxed pl-3 border-l border-slate-700/50">
                    {analysis.validation_reasoning}
                  </p>
                )}
              </>
            )}
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              validateMutation.mutate(analysis.id);
            }}
            disabled={validateMutation.isPending}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-700/50 text-slate-400 hover:bg-indigo-500/20 hover:text-indigo-400 transition-colors disabled:opacity-50"
          >
            {validateMutation.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <ShieldCheck size={12} />
            )}
            {validateMutation.isPending ? 'Validating...' : 'Validate'}
          </button>
        )}
      </div>}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-700/50">
        <span className="flex items-center gap-1">
          <Clock size={12} />
          {timeAgo(analysis.timestamp)}
        </span>
        <span>{analysis.session}</span>
        {analysis.cost_usd !== null && analysis.cost_usd !== undefined && (
          <span>${analysis.cost_usd.toFixed(4)}</span>
        )}
      </div>
    </div>
  );
};

export default SignalCard;
